import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { api } from '../api';
import { errorMessage } from '../errors';
import TaxLookupPanel from '../feeWorksheet/TaxLookupPanel';
import AddressAutocomplete from '../feeWorksheet/AddressAutocomplete';
import { getCounties } from '../feeWorksheet/propertyTaxApi';

const EMPTY_TAX = { tax_unit_ids: null, homestead: false, tax_lookup_status: null, tax_special_answer: null };

// Ticket 131 (PR B): the fee worksheet's tax panel in a dialog, with its own
// address field (the wizard has none). The rate comes from the server through
// the stateless worksheet estimate; nothing here computes it.
export default function WizardTaxLookupDialog({ open, onOpenChange, purchasePrice, onApply }) {
  const [address, setAddress] = useState('');
  const [county, setCounty] = useState('');
  const [counties, setCounties] = useState([]);
  const [value, setValue] = useState(EMPTY_TAX);
  const [taxBasis, setTaxBasis] = useState(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const version = useRef(0); // bumped on every input change; a result for an older version is dropped
  const touch = () => { version.current += 1; setStale(true); };

  useEffect(() => {
    if (!open || counties.length) return;
    getCounties().then((d) => setCounties(d?.counties || [])).catch(() => {});
  }, [open, counties.length]);

  // The rate is worked out at one price; a new price needs a new Calculate.
  useEffect(() => {
    touch();
  }, [purchasePrice]);

  const change = (patch) => { setValue((v) => ({ ...v, ...patch })); touch(); };
  const changeCounty = (c) => {
    if (c === county) return;
    setCounty(c);
    setValue(EMPTY_TAX);
    touch();
  };
  const applyLookup = (patch, c) => {
    setValue((v) => ({ ...v, ...patch }));
    if (c) setCounty(c);
    touch();
  };

  const calculate = async () => {
    const asked = version.current;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/fee-worksheet/estimate', {
        purchase_price: Number(purchasePrice),
        loan_amount: Number(purchasePrice), // required by the endpoint; the tax figure does not depend on it
        state: 'TX',
        county,
        ...value,
      });
      if (version.current !== asked) return; // inputs changed while the request was out
      setTaxBasis(data.tax_basis || null);
      setStale(false);
    } catch (e) {
      if (version.current === asked) setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const lookedUp = taxBasis?.source === 'lookup';
  // Same gate as the worksheet: no rate is final until the special-district question is answered.
  const canApply = lookedUp && Boolean(value.tax_special_answer) && !stale && !busy;
  // Picked taxing units with no answer would calculate a rate the panel will not show.
  const needsAnswer = value.tax_unit_ids != null && !value.tax_special_answer;
  const canCalculate = Boolean(county) && Number(purchasePrice) > 0 && !needsAnswer && !busy;
  let hint = '';
  if (!county) hint = 'Look up the address or pick a county to start.';
  else if (needsAnswer) hint = 'Answer the MUD / special district question above, then click Calculate.';
  else if (!busy && (!taxBasis || stale)) hint = 'Click Calculate to work out the rate.';
  const apply = () => {
    onApply({ rate: taxBasis.effective_rate_pct, label: taxBasis.label });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        data-testid="wizard-tax-dialog"
        // Escape closes an open suggestion list first, not the dialog.
        onEscapeKeyDown={(e) => { if (e.target?.getAttribute?.('aria-expanded') === 'true') e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Look up Texas property taxes</DialogTitle>
          <DialogDescription>Rates come from the Texas Comptroller. The rate is worked out at the purchase price you entered.</DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="wizard-tax-address" className="text-xs text-slate-500">Property address</Label>
          <AddressAutocomplete
            id="wizard-tax-address" testid="wizard-tax-address" value={address} onChange={setAddress} state="TX"
            className="mt-1"
          />
        </div>
        <TaxLookupPanel
          state="TX"
          address={address}
          county={county}
          counties={counties}
          value={value}
          onChange={change}
          onLookupApplied={applyLookup}
          onCountyChange={changeCounty}
          manualRate={null}
          onManualRateChange={() => {}}
          taxBasis={taxBasis}
          stale={stale}
          hideManualRate
        />
        {taxBasis && !stale && !lookedUp && (
          <p className="text-[12px] text-amber-800" data-testid="wizard-tax-not-looked-up">
            This is the county estimate. Pick a school district to apply looked-up rates.
          </p>
        )}
        {error && <p className="text-[12px] text-red-700" data-testid="wizard-tax-error">{error}</p>}
        {hint && <p className="text-[12px] text-amber-800 text-right" data-testid="wizard-tax-hint">{hint}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={calculate} disabled={!canCalculate} className="btn-outline disabled:opacity-40" data-testid="wizard-tax-calculate">
            {busy ? <Loader2 className="animate-spin" size={14} /> : null} Calculate
          </button>
          <button type="button" onClick={apply} disabled={!canApply} className="btn-gold disabled:opacity-40" data-testid="wizard-tax-apply">
            Use this rate
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
